/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from 'react';
import I18n from 'i18n-js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useDebounceValue } from 'usehooks-ts';

import AsyncAssignmentModal from '@/components/staffer/AssignmentModal/AsyncAssignmentModal';
import CreateNewTestButton from '@/components/staffer/assessments/CreateNewTestButton';
import { TCITooltip } from '@/components/ui/Tooltip';
import UnitLessonSelect from '@/components/common/ModelSelect/UnitLessonSelect';
import * as Routes from 'modules/routes';
import { userInfo } from '../actions';
import FilterBar from './FilterBar';

import { getFilteredAssessments, getFilters, initialFilters } from './actions';
import {
  AssessmentFilter,
  AssessmentFilterField,
  AssessmentUnitFilter,
  AssessmentInfo,
  AssignmentFormAssessment,
} from './types';
import styles from './index.module.scss';
import Pagination from '../common/Pagination';
import { FILTER_FETCH_DEBOUNCE_MS, ignoreCancel } from '../common/utils';
import AssessmentRow from './AssessmentRow';

interface Props {
  programId: number;
  lessonId?: number;
  unitId?: number;
}

const AssessmentLibrary = ({
  programId,
  lessonId,
  unitId,
}: Props) => {
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setLoading] = useState(true);
  const [initialDataIsLoaded, setInitialDataIsLoaded] = useState(false);
  const [openedUnitValue, setOpenedUnitValue] = useState(-1);

  const [filters, setFilters] = useState<AssessmentFilter>(initialFilters);
  const [debouncedFilters] = useDebounceValue(filters, FILTER_FETCH_DEBOUNCE_MS);
  const [assessments, setAssessments] = useState<Array<AssessmentInfo>>([]);

  type AssignmentModalSettings = {
    show: boolean;
    lessonId: number;
    assessments: Array<AssignmentFormAssessment> | undefined;
    initialValues: { modelType: string; modelsToAssign: string; }
  };

  const [assignmentModalSettings, setAssignmentModalSettings] = useState<AssignmentModalSettings>({
    assessments: undefined,
    initialValues: { modelType: 'assessment', modelsToAssign: '' },
    lessonId: 0,
    show: false,
  });
  const [currentLesson, setCurrentLesson] = useState<AssessmentFilterField | undefined>(undefined);
  const [currentUnit, setCurrentUnit] = useState<AssessmentUnitFilter | undefined>(undefined);
  const [currentDropdownLesson, setCurrentDropdownLesson] = useState<AssessmentFilterField | undefined>(undefined);

  const currentLessonOrUnitPayload = (() => {
    if (currentLesson) return { lesson_id: currentLesson.value };
    if (currentUnit) return { unit_id: currentUnit.value };

    return {};
  })();

  const findInitialCurrentLesson = (fetchedFilters: AssessmentFilter) => {
    if (!lessonId) return;

    let lesson: AssessmentFilterField | undefined;
    // eslint-disable-next-line array-callback-return
    (fetchedFilters.unit || []).find((unit) => {
      lesson ||= unit.lessons.find(l => l.value === lessonId);
    });

    lesson ||= fetchedFilters.lesson?.find(l => l.value === lessonId);

    return lesson;
  };

  const findInitialCurrentUnit = (fetchedFilters: AssessmentFilter) => {
    if (!unitId) return;

    // eslint-disable-next-line array-callback-return
    return (fetchedFilters.unit || []).find((unit) => {
      if (unit.value === unitId) {
        // eslint-disable-next-line no-return-assign,no-param-reassign
        unit.lessons.forEach(l => l.checked = true);
        return true;
      }
    });
  };

  const fetchFilteredAssessments = () => {
    setLoading(true);
    getFilteredAssessments(filters, programId).then(({ data, count }) => {
      setTotalCount(count);
      setAssessments(data);
      setLoading(false);
    }).catch(ignoreCancel);
  };

  useEffect(() => {
    getFilters(programId, lessonId).then((res) => {
      if (new URLSearchParams(window.location.search).get('unit_id')) {
        setCurrentUnit(findInitialCurrentUnit(res));
      }
      else {
        const lesson = findInitialCurrentLesson(res);
        setCurrentLesson(lesson);
        setCurrentDropdownLesson(lesson);
      }
      setFilters({ ...res });
      setInitialDataIsLoaded(true);
    });
  }, [programId]);

  useEffect(() => {
    fetchFilteredAssessments();
  }, [debouncedFilters, programId]);

  const handleFilterChange = (
    { newFilters, keepTotal, unitValFromFilterBar }:
    { newFilters: AssessmentFilter, keepTotal?: boolean, unitValFromFilterBar?: number },
  ) => {
    if (!keepTotal) setTotalCount(0);
    setOpenedUnitValue(unitValFromFilterBar || -1);
    if (unitValFromFilterBar) {
      setCurrentUnit(undefined);
      setCurrentLesson(undefined);
    }
    setFilters(newFilters);
  };

  const handlePageChange = (curPage: number) => {
    handleFilterChange({ keepTotal: true, newFilters: { ...filters, page: curPage } });
  };

  const paginationResult = totalCount <= filters.per_page
    // eslint-disable-next-line eqeqeq
    ? `${totalCount} result${totalCount == 1 ? '' : 's'}`
    : `${filters.per_page * (filters.page - 1) + 1} - ${Math.min(
      filters.per_page * filters.page,
      totalCount,
    )} of ${totalCount} results`;

  const closeAssignmentModal = () => {
    setAssignmentModalSettings({
      assessments: undefined,
      initialValues: { modelType: 'assessment', modelsToAssign: '' },
      lessonId: 0,
      show: false,
    });
  };

  const handleAssignClick = (
    // eslint-disable-next-line @typescript-eslint/no-shadow
    lessonId: number,
    initialValues: { modelType: string; modelsToAssign: string; },
    // eslint-disable-next-line @typescript-eslint/no-shadow
    assessments: AssignmentFormAssessment[] | undefined,
  ) => {
    setAssignmentModalSettings({ assessments, initialValues, lessonId, show: true });
  };

  return (
    <div className={styles.content}>
      <div className={styles.description}>
        See all the assessments in this program. You can create your own!
      </div>

      <div className={styles.unitLessonSelect}>
        {initialDataIsLoaded && (
          <UnitLessonSelect
            initialOption={currentLesson
              ? { label: currentLesson.label, value: currentLesson.value }
              : currentUnit
                ? { label: currentUnit.label, value: currentUnit.value }
                : { label: 'Select one', value: undefined }
            }
            parentResourceUrl={Routes.library_shared_program_assessments_path(programId, { unit_id: 'select_id' })}
            programId={programId}
            resourceUrl={Routes.library_shared_program_assessments_path(programId, { lesson_id: 'select_id' })}
          />
        )}
      </div>

      <AsyncAssignmentModal
        assessments={assignmentModalSettings.assessments}
        closeModal={closeAssignmentModal}
        handlePostSave={fetchFilteredAssessments}
        initialLessonId={assignmentModalSettings.lessonId}
        initialValues={assignmentModalSettings.initialValues}
        modalIsOpen={assignmentModalSettings.show}
        program={{ id: programId }}
        stafferId={userInfo?.staffer?.id}
      />

      <div className={styles.container}>
        <FilterBar
          filters={filters}
          openedUnitValue={openedUnitValue}
          onFilterChange={handleFilterChange}
        />
        <div className={styles.paginationWrapper}>
          <div className={styles.topBar}>
            {!currentDropdownLesson ? (
              <TCITooltip>
                <TCITooltip.Trigger asChild>
                  <div>
                    <CreateNewTestButton
                      disabled
                      ajaxPath={Routes.staffer_assessments_path({
                        lesson_id: currentDropdownLesson?.value || 0, program_id: programId,
                      })}
                      lessonTitle={currentDropdownLesson?.title}
                    />
                  </div>
                </TCITooltip.Trigger>
                <TCITooltip.Content>
                  {I18n.t('libraries.assessments.select_lesson_to_create_test')}
                </TCITooltip.Content>
              </TCITooltip>
            ) : (
              <CreateNewTestButton
                ajaxPath={Routes.staffer_assessments_path({
                  lesson_id: currentDropdownLesson.value, program_id: programId,
                })}
                lessonTitle={currentDropdownLesson.title}
              />
            )}
            {!isLoading && <div>{paginationResult}</div>}
          </div>

          {/* eslint-disable-next-line no-nested-ternary */}
          {!isLoading && assessments.length > 0 ? (
            <div className={styles.table}>
              <div className={styles.tableHeader}>
                <div className={styles.titleColumn}>assessment title</div>
                <div className={styles.locationColumn}>location</div>
                <div className={styles.categoryColumn}>category</div>
                <div className={styles.sourceColumn}>source</div>
                <div className={styles.questionColumn}># of questions</div>
                <div className={styles.actionColumn} />
              </div>

              {assessments.map(assessment => (
                <AssessmentRow
                  key={assessment.id}
                  assessment={assessment}
                  currentLessonOrUnitPayload={currentLessonOrUnitPayload}
                  programId={programId}
                  onAssignClick={handleAssignClick}
                />
              ))}
            </div>
          ) : isLoading ? (
            <div className={styles.loading}>
              <FontAwesomeIcon spin icon={faSpinner} />
            </div>
          ) : (
            <div className={styles.noResult}>
              <div className={styles.noResultTitle}>No Assessments Found</div>
              <div className={styles.noResultDescription}>
                Please modify your filters to explore different results.
              </div>
            </div>
          )}

          <div className={styles.bottomPagination}>
            <Pagination
              currentPage={filters.page}
              pageSize={filters.per_page}
              totalCount={totalCount}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentLibrary;
